import Lenis from "lenis";

/**
 * Single source of truth for Lenis smooth-scroll on the join (landing) pages.
 *
 * Why this exists: every layout used to do `new Lenis({ duration, easing })`
 * with no touch config. Lenis v1 defaults to `syncTouch: false`, which lets the
 * browser run native momentum scrolling while Lenis keeps its own (now stale)
 * `targetScroll`. On touch release the engine snapped back to that stale target —
 * the "scroll jumps backwards on mobile" bug. Enabling `syncTouch` makes Lenis
 * own touch scrolling too, so target and actual position stay in sync.
 *
 * Pass per-layout overrides (e.g. `duration`, `easing`) — the touch defaults are
 * applied first and can still be overridden if a layout truly needs to.
 */
export function createLenis(overrides: ConstructorParameters<typeof Lenis>[0] = {}): Lenis {
  return new Lenis({
    syncTouch: true,
    touchMultiplier: 1.5,
    ...overrides,
  });
}

function isFormField(el: EventTarget | null): boolean {
  return (
    el instanceof HTMLElement &&
    (el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.tagName === "SELECT" ||
      el.isContentEditable)
  );
}

/**
 * Mobil klavye ↔ smooth-scroll çakışmasını çözer (RSVP formundaki "bir ileri
 * bir geri" zıplama + odak yanlış yere gidiyor bug'ı).
 *
 * Bir form alanı odaklanınca ekran klavyesi açılır ve tarayıcı alanı görünüme
 * getirmek için sayfayı NATIVE olarak kaydırır. Lenis ise kendi (artık bayat)
 * hedefine doğru RAF döngüsünde kaydırmaya devam eder → iki motor çekişir, alan
 * zıplar. Alan odaktayken Lenis'i durdurmak kaydırmayı tarayıcıya bırakır:
 * native scroll-into-view bir kez oturur ve kalır (Lenis `stop()` içinde
 * `reset()` ile iç konumunu gerçek scroll'a eşitler, `onNativeScroll` da senkron
 * tutar). Odak son alandan çıkınca `start()` temiz devam ettirir — snap yok.
 *
 * Dinleyicileri kaldıran cleanup döner.
 */
export function bindKeyboardScrollFix(lenis: Lenis, root: Document = document): () => void {
  const onFocusIn = (e: FocusEvent) => {
    if (isFormField(e.target)) lenis.stop();
  };
  const onFocusOut = (e: FocusEvent) => {
    if (!isFormField(e.target)) return;
    // Odak başka bir alana geçtiyse (klavye açık kalıyor) durdurulmuş kal;
    // yalnız gerçekten alandan çıkınca smooth-scroll'u geri aç.
    if (isFormField(e.relatedTarget)) return;
    lenis.start();
  };

  root.addEventListener("focusin", onFocusIn);
  root.addEventListener("focusout", onFocusOut);
  return () => {
    root.removeEventListener("focusin", onFocusIn);
    root.removeEventListener("focusout", onFocusOut);
  };
}
